import { defaultSettings, validateSettings } from '@playcanvas/splat-transform/viewer-settings';
import { Color, Vec3 } from 'playcanvas';

import type { Pose } from './camera-poses';
import type { Events } from './events';
import type { JsiSceneData } from './jsi-scene-data';
import { applySceneLayers, layersFromSettings, readScenePackage, writeScenePackage, type SceneLayerValues } from './jsi-scene-package';
import { canonicalSceneSettings } from './scene-data-camera';

export const registerEditorSceneData = (events: Events) => {
    let id: string = crypto.randomUUID();
    let metadata: JsiSceneData | undefined;
    let retained: Record<string, unknown> = {};
    let applying = false;
    const pack = (v: Vec3) => [v.x, v.y, v.z];
    const snapshot = (): Record<string, unknown> => {
        const pose = events.invoke('camera.getPose');
        const bg = events.invoke('bgClr');
        const poses: Pose[] = events.invoke('camera.animTrack').snapshot();
        const ordered = [...poses].sort((a, b) => a.frame - b.frame);
        const world = ordered.map(p => events.invoke('camera.transformPose', p));
        const fps = events.invoke('timeline.frameRate');
        const animTracks = ordered.length ? [{
            name: 'cameraAnim',
            duration: Math.max(events.invoke('timeline.frames'), ordered.at(-1).frame + 1) / fps,
            frameRate: fps,
            loopMode: 'repeat',
            interpolation: 'spline',
            smoothness: events.invoke('timeline.smoothness'),
            keyframes: { times: ordered.map(p => p.frame),
                values: {
                    position: world.flatMap(p => pack(p.position)), target: world.flatMap(p => pack(p.target)), fov: ordered.map(p => p.fov ?? 60)
                } }
        }] : [];
        const channel = events.invoke('scene.allSplats').find((s: { resource: { customChannelName: string | null } }) => s.resource.customChannelName)?.resource.customChannelName;
        return canonicalSceneSettings({
            ...defaultSettings(),
            ...retained,
            ...(channel ? { customChannelProperty: channel } : {}),
            customChannelColormap: events.invoke('view.customChannelColormap'),
            background: { ...(retained.background as object ?? {}), color: [bg.r, bg.g, bg.b] },
            spatialGuides: events.invoke('spatialGuides'),
            annotations: events.invoke('docSerialize.annotations'),
            cameraPoses: poses.map((p, i) => ({
                ...p,
                id: p.id ?? `camera-${i}`,
                label: p.label ?? p.name,
                position: pack(p.position),
                target: pack(p.target),
                fov: p.fov ?? 60
            })),
            cameraPosesSpace: 'viewer',
            cameraPosesTransform: events.invoke('camera.poseTransform'),
            cameras: pose ? [{ initial: { position: pack(pose.position), target: pack(pose.target), fov: pose.fov } }] : [],
            animTracks: retained.animTracks ?? animTracks,
            startMode: retained.startMode ?? (animTracks.length ? 'animTrack' : 'default')
        });
    };
    const capture = () => ({
        id,
        metadata,
        retained: structuredClone(retained),
        guides: events.invoke('spatialGuides'),
        annotations: events.invoke('docSerialize.annotations'),
        poses: events.invoke('camera.animTrack').snapshot() as Pose[],
        poseTransform: events.invoke('camera.poseTransform'),
        timeline: events.invoke('docSerialize.timeline'),
        camera: events.invoke('camera.getPose'),
        background: events.invoke('bgClr').clone(),
        colormap: events.invoke('view.customChannelColormap')
    });
    const apply = (state: ReturnType<typeof capture>) => {
        applying = true;
        try {
            events.fire('measurement.cancel');
            events.fire('tool.deactivate');
            id = state.id; metadata = state.metadata; retained = structuredClone(state.retained);
            events.invoke('docDeserialize.spatialGuides', state.guides);
            events.invoke('docDeserialize.annotations', state.annotations);
            events.invoke('docDeserialize.timeline', state.timeline);
            events.fire('camera.setPoseTransform', state.poseTransform);
            events.fire('camera.loadPoses', state.poses);
            events.fire('setBgClr', state.background);
            events.fire('view.customChannelColormap.set', state.colormap);
            if (state.camera) events.fire('camera.setPose', state.camera, 0);
        } finally {
            applying = false;
        }
    };
    const prepare = (layers: SceneLayerValues, manifest: JsiSceneData) => {
        if (layers.attribution?.length) throw new Error('Load the matching SOG/PLY asset to restore attribution values. Attribution-only companion import into edited editor resources is not supported yet.');
        const before = capture();
        const merged = applySceneLayers(snapshot(), layers);
        validateSettings(merged);
        const after = { ...before, id: manifest.scene.id, metadata: manifest, retained: merged };
        if (typeof layers.presentation?.customChannelColormap === 'string') after.colormap = layers.presentation.customChannelColormap;
        if (layers.references) after.guides = layers.references;
        if (layers.annotations) after.annotations = layers.annotations;
        if (layers['camera-poses']) {
            const canonical = canonicalSceneSettings(merged);
            after.poses = (canonical.cameraPoses as Record<string, unknown>[]).map((p, i) => ({
                ...p,
                name: String(p.label ?? p.id),
                frame: typeof p.frame === 'number' ? p.frame : i * before.timeline.frameRate,
                position: new Vec3(p.position as number[]),
                target: new Vec3(p.target as number[]),
                fov: Number(p.fov)
            }));
            after.poseTransform = { position: [0, 0, 0], rotation: [0, 0, 0], scale: 1 };
            after.timeline = { ...before.timeline, frames: Math.max(before.timeline.frames, ...after.poses.map(p => p.frame + 1)) };
            const initial = (merged.cameras as { initial: { position: number[]; target: number[]; fov: number } }[])?.[0]?.initial;
            if (initial) after.camera = { position: new Vec3(initial.position), target: new Vec3(initial.target), fov: initial.fov };
        }
        if (layers.presentation?.background) {
            after.background = new Color((layers.presentation.background as { color: number[] }).color);
        }
        return { before, after };
    };
    events.function('sceneData.export', () => writeScenePackage(layersFromSettings(snapshot()), id, [], metadata));
    events.function('sceneData.viewerDefaults', () => {
        const settings = snapshot();
        return { ...layersFromSettings(settings).presentation,
            cameraPosesInvertDirection: settings.cameraPosesInvertDirection,
            cameraOverlayIconUrl: settings.cameraOverlayIconUrl,
            cameraPosesScale: settings.cameraPosesScale,
            cameraPosesLineWidth: settings.cameraPosesLineWidth,
            cameraPosesOpacity: settings.cameraPosesOpacity,
            cameraPosesInactiveOpacity: settings.cameraPosesInactiveOpacity };
    });
    events.function('sceneData.import', async (value: unknown) => {
        const incoming = await readScenePackage(value);
        const state = prepare(incoming.layers, incoming.manifest);
        const result = await events.invoke('showPopup', {
            type: 'okcancel',
            header: 'Import JSI Scene Data',
            message: 'Replace the included data layers? The editor keeps its current geometry and scale. Continue only if these coordinates already match the scene; source alignment is not inferred.'
        });
        if (result.action !== 'ok') return false;
        events.fire('edit.add', { name: 'importJsiSceneData', do: () => apply(state.after), undo: () => apply(state.before) });
        return true;
    });
    events.function('docSerialize.jsiSceneData', () => writeScenePackage(layersFromSettings(snapshot()), id, [], metadata));
    events.function('docDeserialize.jsiSceneData', async (value: unknown) => {
        if (!value) return;
        const incoming = await readScenePackage(value);
        apply(prepare(incoming.layers, incoming.manifest).after);
    });
    events.on('scene.clear', () => {
        id = crypto.randomUUID(); metadata = undefined; retained = {};
    });
    const invalidateAnimation = () => {
        if (!applying) {
            delete retained.animTracks; delete retained.startMode;
        }
    };
    for (const event of ['track.keyAdded', 'track.keyRemoved', 'track.keyUpdated', 'track.keysLoaded', 'track.keysCleared', 'timeline.frames', 'timeline.frameRate', 'timeline.smoothness', 'camera.poseTransform']) {
        events.on(event, invalidateAnimation);
    }
};
